import clsx from 'clsx';
import type { ChangeEvent, FocusEvent, KeyboardEvent, Ref } from 'react';
import { useId } from 'react';

import { SDSInputErrorMessage } from '@repo/sds/components/SDSInputErrorMessage';
import { SDSInputLabel } from '@repo/sds/components/SDSInputLabel';
import type { SDSTextType } from '@repo/sds/components/SDSText';

import '@repo/sds/components/SDSTextArea/SDSTextArea.scss';

export interface SDSTextAreaProps {
  errorMessage?: string;
  className?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  /** Typography of the label above the field; defaults to the SDS label style. */
  labelTextType?: SDSTextType;
  name?: string;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  value?: string;
  isFullWidth?: boolean;
  rows?: number;
  maxLength?: number;
  dataTestId?: string;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  ref?: Ref<HTMLTextAreaElement>;
}

export function SDSTextArea({
  errorMessage,
  className,
  isDisabled,
  isRequired,
  label,
  labelTextType,
  name,
  onBlur,
  onChange,
  placeholder,
  value = '',
  isFullWidth,
  rows = 4,
  maxLength,
  dataTestId,
  ariaDescribedBy,
  ariaLabel,
  onKeyDown,
  ref,
}: SDSTextAreaProps) {
  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event.target.value);
  };

  const textareaClassName = clsx(
    'SDSTextArea',
    {
      'SDSTextArea--error': errorMessage,
      'SDSTextArea--fullWidth': isFullWidth,
      'SDSTextArea--disabled': isDisabled,
    },
    className,
  );

  const id = useId();
  const errorId = errorMessage ? `${id}-error` : undefined;

  return (
    <label
      htmlFor={id}
      className={clsx('SDSTextArea__container', { 'SDSTextArea__container--fullWidth': isFullWidth })}
    >
      <SDSInputLabel label={label} isRequired={isRequired} textType={labelTextType} />
      <textarea
        id={id}
        ref={ref}
        name={name}
        value={value}
        placeholder={placeholder}
        rows={rows}
        disabled={isDisabled}
        onChange={handleChange}
        onBlur={onBlur}
        className={textareaClassName}
        maxLength={maxLength}
        data-testid={dataTestId}
        aria-describedby={[ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined}
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
      />
      <SDSInputErrorMessage id={errorId} dataTestId={`${dataTestId}-error`} errorMessage={errorMessage} />
    </label>
  );
}
